// Peak Plumbing — 8 Core Service Categories.
// This structure follows Google's guidance on avoiding Scaled Content Abuse:
// instead of separate pages for every micro-service (e.g. faucet-replacement,
// toilet-repair, hose-bib-repair etc.), each core category consolidates its
// related micro-services into H2/H3 sub-sections within one authoritative
// per-city page.

export const CORE_SERVICES = [
  {
    slug: "drain-cleaning",
    title: "Drain Cleaning & Hydro-Jetting",
    shortTitle: "Drain Cleaning",
    icon: "Waves",
    blurb:
      "Clogged drains and slow flow diagnosed, cleared and camera-inspected — from a single sink to a full main line.",
    covers: [
      {
        name: "Main line backups",
        detail:
          "Full-house drain slowdowns almost always trace back to a compromised main line. We clear them same-day with the right cutter for the blockage.",
      },
      {
        name: "Kitchen drain clogs",
        detail:
          "Grease, food debris and biofilm cause more kitchen drain problems than anything else. We remove the buildup rather than just poking a hole through it.",
      },
      {
        name: "Hydro-jetting",
        detail:
          "High-pressure water jetting scours the entire pipe wall — restoring 100% of the original inside diameter. Ideal for grease and root buildup.",
      },
      {
        name: "Camera inspections",
        detail:
          "HD sewer cameras show you exactly what's happening underground so you never pay for a repair you don't need.",
      },
      {
        name: "Root removal",
        detail:
          "Mechanical root cutting and chemical foaming stop tree roots from returning through cracks in older clay and cast iron pipes.",
      },
    ],
    keywords: [
      "drain cleaning",
      "clogged drain",
      "hydro jetting",
      "sewer camera inspection",
      "kitchen drain clog",
      "root removal",
    ],
  },
  {
    slug: "water-heaters",
    title: "Water Heater Services",
    shortTitle: "Water Heaters",
    icon: "Flame",
    blurb:
      "New water heater installs, replacements and service — from standard 40-gallon tanks to whole-home tankless systems.",
    covers: [
      {
        name: "Tankless installation",
        detail:
          "Endless hot water, lower energy bills and much longer service life. Every install includes proper venting and gas-line sizing.",
      },
      {
        name: "Tank replacement",
        detail:
          "Same-day 40-, 50- and 75-gallon replacements. Old unit hauled away, area left cleaner than we found it.",
      },
      {
        name: "Flushing & maintenance",
        detail:
          "Annual sediment flushing keeps tank heaters efficient and doubles service life. We recommend it once a year — twice on well water.",
      },
      {
        name: "Anode rod replacement",
        detail:
          "The sacrificial anode is what stops your tank from rusting. Swapping it every 3–5 years is the single best thing you can do for tank longevity.",
      },
      {
        name: "Thermostat & element repair",
        detail:
          "Not enough hot water? Water too hot? Nine times out of ten, it's a thermostat or heating element — usually a same-visit fix.",
      },
    ],
    keywords: [
      "water heater",
      "hot water heater",
      "tankless water heater",
      "water heater repair",
      "gas water heater",
      "electric water heater",
    ],
  },
  {
    slug: "sewer-line-repair",
    title: "Sewer & Main Line Repair",
    shortTitle: "Sewer Repair",
    icon: "Hammer",
    blurb:
      "Traditional excavation sewer and main-line repair done with careful, thorough workmanship — restoring your line and your yard.",
    covers: [
      {
        name: "Excavation sewer repair",
        detail:
          "When a section of your sewer main is cracked, offset or collapsed, we open it up, replace the failed section, and backfill properly so you don't have settling issues next spring.",
      },
      {
        name: "Main line replacement",
        detail:
          "Full main line replacement from the house to the city tap when your existing pipe is beyond repair. Permitted, inspected and warrantied.",
      },
      {
        name: "Water service line leaks",
        detail:
          "Incoming water service leaks between the meter and your home — repaired or fully replaced with copper or PEX depending on soil and code.",
      },
      {
        name: "Pipe replacement",
        detail:
          "Sections of failing galvanized, cast iron or clay pipe replaced with modern PVC or PEX. Cleaner flow and decades of added service life.",
      },
    ],
    keywords: [
      "sewer line repair",
      "main line repair",
      "sewer replacement",
      "water service line leak",
      "cracked sewer pipe",
    ],
  },
  {
    slug: "water-treatment-well-services",
    title: "Water Conditioning, Filtration & Well Services",
    shortTitle: "Water Treatment & Wells",
    icon: "FlaskConical",
    blurb:
      "Soft, clean water and reliable well systems — from whole-house filtration to submersible well pumps.",
    covers: [
      {
        name: "Submersible well pumps",
        detail:
          "Install, service and replace submersible well pumps. Choosing the right pump for your well depth and demand is what makes it last.",
      },
      {
        name: "Well pressure tanks",
        detail:
          "Pressure tank replacement to restore proper cycling and stop short-cycling that burns out pumps early.",
      },
      {
        name: "Pressure switches",
        detail:
          "The pressure switch is the most common failure point in a well system — quick replacement usually brings water back the same visit.",
      },
      {
        name: "Water softeners",
        detail:
          "Whole-home softeners sized to your water hardness and household demand — proper sizing is what makes them worth the investment.",
      },
      {
        name: "Whole-house filtration",
        detail:
          "Chlorine, sediment, VOC and heavy-metal filtration installed at the point of entry so every faucet delivers clean water.",
      },
      {
        name: "Iron & sulfur filters",
        detail:
          "Specialty filtration for the classic Michigan well problems — rust staining, rotten-egg smell, and iron bacteria.",
      },
    ],
    keywords: [
      "water softener",
      "well pump",
      "well repair",
      "water conditioning",
      "whole house filtration",
      "iron filter",
      "reverse osmosis",
    ],
  },
  {
    slug: "leak-detection",
    title: "Leak Detection & Repair",
    shortTitle: "Leak Detection",
    icon: "Droplets",
    blurb:
      "Locate hidden leaks non-invasively and repair them cleanly — before they wreck drywall, subfloors or foundations.",
    covers: [
      {
        name: "Slab leaks",
        detail:
          "Warm floor spots, unexplained water bills or damp carpet edges usually mean a slab leak. Electronic detection pinpoints the location before we open anything.",
      },
      {
        name: "Hidden pipe leaks",
        detail:
          "In-wall and behind-cabinet leaks located with acoustic and thermal tools. We open only the drywall we absolutely have to.",
      },
      {
        name: "Ceiling water damage",
        detail:
          "Track a wet spot back to its actual source — usually not directly above the stain. Fix the root cause, then patch.",
      },
      {
        name: "Copper & PEX pipe repair",
        detail:
          "Pin-hole leaks, split lines and freeze breaks repaired properly — no push-fit shortcuts on hidden connections.",
      },
      {
        name: "Burst pipes",
        detail:
          "Isolate, drain, cut back to good pipe and rebuild. Documented for insurance where needed.",
      },
    ],
    keywords: [
      "leak detection",
      "hidden leak",
      "slab leak",
      "burst pipe",
      "pipe leak repair",
      "ceiling leak",
    ],
  },
  {
    slug: "fixture-repair",
    title: "Fixture Repair & Installation",
    shortTitle: "Fixtures",
    icon: "Wrench",
    blurb:
      "Every faucet, sink, toilet and disposal in your home — repaired, replaced or newly installed.",
    covers: [
      {
        name: "Kitchen faucets",
        detail:
          "Install pull-down, sprayer, touchless or filtered kitchen faucets. Repair drips, low flow or leaking bases without replacing the whole fixture.",
      },
      {
        name: "Bathroom sinks",
        detail:
          "Vanity sink installs, pop-up assembly repair, supply-line replacement and full sink swaps.",
      },
      {
        name: "Toilets",
        detail:
          "Toilet repair, replacement, wax-ring resets, running-toilet fixes and full replacement with modern water-saving models.",
      },
      {
        name: "Garbage disposals",
        detail:
          "Install, unjam and replace garbage disposals. Most jams are a same-visit fix.",
      },
      {
        name: "Shower valves",
        detail:
          "Cartridge replacement, valve upgrades and full valve replacements when temperature control is failing.",
      },
      {
        name: "Hose bibbs",
        detail:
          "Frost-proof outdoor hose bibb install and repair — protect the fixture and the wall behind it from Michigan freezes.",
      },
    ],
    keywords: [
      "faucet replacement",
      "toilet repair",
      "garbage disposal",
      "kitchen faucet",
      "bathroom faucet",
      "shower valve",
      "hose bibb",
    ],
  },
  {
    slug: "sump-pumps",
    title: "Sump Pump & Basement Protection",
    shortTitle: "Sump Pumps",
    icon: "ArrowDownToLine",
    blurb:
      "Keep your basement dry — primary sump pumps, battery backups, ejector pumps and discharge line maintenance.",
    covers: [
      {
        name: "Primary sump pumps",
        detail:
          "Sized to your pit, groundwater flow and horsepower needs. Cheaper pumps fail exactly when you need them most.",
      },
      {
        name: "Battery backup systems",
        detail:
          "Storm-season insurance. Backup pumps run when the power's out — which is exactly when the main breaker trips.",
      },
      {
        name: "Discharge lines",
        detail:
          "Clogged, frozen or improperly-pitched discharge lines cause more pump 'failures' than the pumps themselves. We inspect and repair.",
      },
      {
        name: "Sewage ejector pumps",
        detail:
          "Below-grade bathroom, laundry and basement fixture ejector pump installs, repair and replacement.",
      },
    ],
    keywords: [
      "sump pump",
      "sump pump replacement",
      "battery backup sump pump",
      "sewage ejector pump",
      "basement flood",
    ],
  },
  {
    slug: "emergency-plumbing",
    title: "Emergency Plumbing",
    shortTitle: "Emergency",
    icon: "AlarmClock",
    blurb:
      "Sudden flooding, burst pipes and main shutoff failures — rapid dispatch when you need it most.",
    covers: [
      {
        name: "Rapid dispatch",
        detail:
          "Our office team routes urgent calls to the nearest available technician so we can be on-site as quickly as possible during our operating hours.",
      },
      {
        name: "Sudden flooding",
        detail:
          "Water shutoff, source containment, damage-limiting first response — stabilize the situation before it gets worse.",
      },
      {
        name: "Frozen & burst pipes",
        detail:
          "Safe thawing, isolation of failed sections, and quick permanent repairs. Documented for insurance if needed.",
      },
      {
        name: "Main shutoff valve failures",
        detail:
          "Curb-stop and interior main shutoff repairs so you regain the ability to isolate your water supply when things go wrong.",
      },
    ],
    keywords: [
      "emergency plumber",
      "burst pipe",
      "frozen pipe",
      "flooding",
      "main shutoff valve",
    ],
  },
];

// Flat list & slug lookup — used by routing, sitemap and prerender
export const CORE_SERVICE_MAP = CORE_SERVICES.reduce((acc, s) => {
  acc[s.slug] = s;
  return acc;
}, {});

// Backward-compat exports (some older components still import these names)
export const SERVICES = CORE_SERVICES;
export const SERVICE_MAP = CORE_SERVICE_MAP;
export const FEATURED_SERVICES = CORE_SERVICES;
export const FEATURED_SERVICE_SLUGS = CORE_SERVICES.map((s) => s.slug);
